import { useEffect, useState } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { Card, Modal, ModalBody, Table } from "reactstrap";
import avatar from "../../assets/imgs/avatar-img.png";
import FormInput from "../formInput/FormInput";

const ItemSelectionInvoiceModal = ({
  modal,
  toggle,
  line_items,
  setLineItems,
  state,
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);

  const handleQuantityChange = (index, newQuantity) => {
    const updatedItems = [...line_items];
    updatedItems[index].quantity = newQuantity;
    setLineItems(updatedItems);

    const itemToUpdate = updatedItems[index];
    const selectedIndex = selectedItems.findIndex(
      (item) => item.sku === itemToUpdate.sku
    );
    if (selectedIndex !== -1) {
      const updatedSelectedItems = [...selectedItems];
      updatedSelectedItems[selectedIndex] = itemToUpdate;
      setSelectedItems(updatedSelectedItems);
    }
  };

  const handleCheckboxChange = (index, row) => {
    const selectedIndex = selectedItems.findIndex(
      (item) => item.sku === row.sku
    );
    if (selectedIndex === -1) {
      setSelectedItems([...selectedItems, row]);
    } else {
      const updatedSelectedItems = [...selectedItems];
      updatedSelectedItems.splice(selectedIndex, 1);
      setSelectedItems(updatedSelectedItems);
    }
  };

  const allSelected = line_items?.length > 0 && selectedItems.length === line_items.length;
  const someSelected = selectedItems.length > 0 && selectedItems.length < (line_items?.length ?? 0);

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems([...(line_items ?? [])]);
    }
  };

  useEffect(() => {
    const subtotalCalc = selectedItems.reduce((acc, item) => {
      const itemTotal = Number(item.price ?? 0) * Number(item.quantity ?? 0);
      return acc + itemTotal;
    }, 0);
    setSubTotal(subtotalCalc);

    const taxRate = parseFloat(state?.totalTaxAmount?.tax ?? 0);
    const shipping = parseFloat(state?.totalTaxAmount?.shippingAmount ?? 0);
    const insurance = parseFloat(state?.totalTaxAmount?.insurance ?? 0);
    const discount = parseFloat(state?.totalTaxAmount?.discount ?? 0);

    const calculatedTax = (subtotalCalc / 100) * taxRate;
    setTax(calculatedTax);

    const totalCalc =
      subtotalCalc + calculatedTax + shipping + insurance - discount;
    setTotal(totalCalc);
  }, [selectedItems, state?.totalTaxAmount]);

  return (
    <>
      <Modal isOpen={modal} toggle={toggle} centered size="xl">
        <ModalBody style={{ padding: "25px" }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3" style={{ borderBottom: "2px solid #e2e8f0", paddingBottom: "12px" }}>
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex flex-column gap-1">
                <div style={{ width: "24px", height: "4px", backgroundColor: "#3b5998", borderRadius: "2px" }} />
                <div style={{ width: "24px", height: "4px", backgroundColor: "#dc2626", borderRadius: "2px" }} />
              </div>
              <h4 className="m-0 fw-bold" style={{ color: "#1a1a2e", letterSpacing: "0.3px" }}>Generate Invoice</h4>
              {selectedItems?.length > 0 && (
                <span className="ms-2 px-2 py-1 rounded-pill" style={{ backgroundColor: "#fee2e2", color: "#dc2626", fontSize: "12px", fontWeight: 700 }}>
                  {selectedItems.length} selected
                </span>
              )}
            </div>
            <IoCloseSharp
              size={28}
              onClick={toggle}
              className="pointer"
              style={{ cursor: "pointer", color: "#6b7280" }}
            />
          </div>

          {/* Table */}
          <div style={{ borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <Table
              responsive
              bordered
              hover
              className="text-center mb-0"
              style={{ fontSize: "13px" }}
            >
              <thead>
                <tr style={{ backgroundColor: "#2d4a7a", color: "#fff" }}>
                  <th style={{ width: "60px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    <div className="d-flex flex-column align-items-center gap-1">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#fff" }}
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={handleSelectAll}
                        title={allSelected ? "Deselect All" : "Select All"}
                      />
                      <span style={{ fontSize: "9px", lineHeight: 1 }}>All</span>
                    </div>
                  </th>
                  <th style={{ width: "70px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Image</th>
                  <th style={{ width: "120px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>SKU</th>
                  <th style={{ width: "240px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left" }}>Description</th>
                  <th style={{ width: "90px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Part</th>
                  <th style={{ width: "80px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Weight</th>
                  <th style={{ width: "90px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Price</th>
                  <th style={{ width: "120px", padding: "10px 8px", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Quantity</th>
                </tr>
              </thead>

              <tbody>
                {line_items?.map((row, index) => {
                  const isSelected = selectedItems.some((item) => item.sku === row.sku);
                  return (
                    <tr
                      key={index}
                      className="align-middle"
                      style={{
                        backgroundColor: isSelected ? "#eef2ff" : index % 2 === 0 ? "#fff" : "#f8fafc",
                        borderLeft: isSelected ? "3px solid #3b5998" : "3px solid transparent",
                        borderBottom: "1px solid #e2e8f0",
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      <td style={{ padding: "8px" }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          style={{ width: "16px", height: "16px", cursor: "pointer" }}
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(index, row)}
                        />
                      </td>

                      <td style={{ padding: "8px" }}>
                        <img
                          src={row?.image?.src ?? avatar}
                          height={36}
                          width={36}
                          className="rounded"
                          style={{ objectFit: "cover", border: "1px solid #e2e8f0" }}
                          alt="product"
                        />
                      </td>

                      <td style={{ fontWeight: 600, color: "#1e293b", padding: "8px" }}>{row?.sku}</td>

                      <td style={{ textAlign: "left", padding: "8px", color: "#475569" }}>
                        {row?.description?.slice(0, 45) ?? "N/A"}...
                      </td>

                      <td style={{ padding: "8px", color: "#64748b" }}>{row?.sku?.slice(4) ?? 0}</td>

                      <td style={{ padding: "8px", color: "#64748b" }}>{row?.weight ?? 0}</td>

                      <td style={{ padding: "8px", fontWeight: 700, color: "#1e40af" }}>
                        ${Number(row?.price ?? 0).toFixed(2)}
                      </td>

                      <td style={{ width: "120px", padding: "8px" }}>
                        <div className="d-flex flex-column align-items-center gap-1">

                          <FormInput
                            size={"small"}
                            type={"number"}
                            className={"w-75 text-center"}
                            value={row?.quantity}
                            handleChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (value >= 0) {
                                handleQuantityChange(index, value);
                              }
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Totals Section */}
          {selectedItems.length > 0 && (
            <div className="d-flex justify-content-end mt-3">
              <Card className="border-0 shadow-sm" style={{ width: "320px", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0 !important" }}>
                <div className="bg-light p-2 text-center border-bottom">
                  <span className="fw-bold text-uppercase" style={{ fontSize: "12px", color: "#3b5998", letterSpacing: "0.5px" }}>Order Summary</span>
                </div>
                <div className="p-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Sub Total</span>
                    <span className="fw-semibold">${subTotal.toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Tax</span>
                    <span className="fw-semibold">${tax.toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Shipping</span>
                    <span className="fw-semibold">${parseFloat(state?.totalTaxAmount?.shippingAmount ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Insurance</span>
                    <span className="fw-semibold">${parseFloat(state?.totalTaxAmount?.insurance ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Discount</span>
                    <span className="fw-semibold text-danger">-${parseFloat(state?.totalTaxAmount?.discount ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between pt-2 border-top">
                    <span className="fw-bold" style={{ fontSize: "16px" }}>Total</span>
                    <span className="fw-bold text-primary" style={{ fontSize: "16px" }}>${total.toFixed(2)}</span>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Footer Action */}
          <div className="d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: "1px solid #e2e8f0" }}>
            <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
              {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""} selected for invoice
            </p>
            <Link
              to={{
                pathname: `/admin/invoice-pdf/`,
                state: {
                  ...state,
                  line_items: selectedItems,
                },
              }}
              className="text-white text-decoration-none px-4 py-2 fw-semibold rounded-2 d-flex align-items-center gap-2"
              style={{
                backgroundColor: selectedItems.length > 0 ? "#3b5998" : "#94a3b8",
                fontSize: "14px",
                letterSpacing: "0.3px",
                opacity: selectedItems.length > 0 ? 1 : 0.7,
                pointerEvents: selectedItems.length > 0 ? "auto" : "none",
                transition: "all 0.2s ease",
              }}
            >
              Generate Invoice
            </Link>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ItemSelectionInvoiceModal;
